/**
 * ⚠️ LA regla única del `id_company` que viaja al back de Payments. TypeScript puro.
 *
 * El `id` de cada fila de `GET /api/v1/payments/companies/` es el número INTERNO de
 * Payments y NINGUNA ruta lo acepta como `id_company` (el back no encuentra el
 * comercio y responde 403 "sin permiso" o 404, aunque el usuario sí tenga acceso).
 * Lo que se manda es, en este orden (reporte del back, 2026-10-16):
 *
 *   1. `org_company_id` (PK de la empresa de banca), si no es null/vacío;
 *   2. si no, `external_ref` (UUID del tenant), si no es vacío;
 *   3. si no, NINGUNO: falla cerrada. La acción que lo necesita no se ofrece y no
 *      sale ninguna petición. Jamás se cae al `id` interno ni a un valor inventado.
 *
 * La entrada NO tiene campo para el `id` interno a propósito: no hay forma de
 * pasárselo por error. La consumen el selector de comercio del cobro QR
 * (`resolveQrCompanyId`) y las rutas de credenciales (`toPaymentsCompanyHandle`).
 *
 * El `id_company` que traen las filas de links, cargos y QR, y el `payments_company`
 * de `GET /api/gestor/services/`, YA es el correcto: esos no pasan por aquí.
 */

/** Identificador de comercio que acepta el back: PK de banca (número) o UUID (texto). */
export type PaymentsCompanyId = number | string;

/** Entrada estructural: el dominio no importa `shared/models`. */
export interface PaymentsCompanyIdSource {
  readonly orgCompanyId?: number | string | null;
  readonly externalRef?: string | null;
}

/**
 * Devuelve el valor SIN convertir de tipo: un `org_company_id` numérico sale número
 * (entero ≥ 0; otro número no es un PK y cuenta como ausente) y uno textual sale
 * texto recortado. `null` = no hay nada que mandar.
 */
export function resolvePaymentsCompanyId(source: PaymentsCompanyIdSource): PaymentsCompanyId | null {
  const org = source.orgCompanyId;
  if (typeof org === 'number' && Number.isInteger(org) && org >= 0) return org;
  if (typeof org === 'string' && org.trim() !== '') return org.trim();
  const ext = source.externalRef;
  if (typeof ext === 'string' && ext.trim() !== '') return ext.trim();
  return null;
}
